// Season stats — pure aggregation over a list of routine placements.
//
// Used by the admin season-stats panel and the parent "season recap" tile.
// Operates on already-joined rows (we don't fetch here); the UI does the
// query and feeds the result in.

export interface PlacementRow {
  competition_routine_id: string;
  competition_id: string;
  competition_name: string;
  dance_id: string;
  dance_name: string;
  placement: number | null;
  total_competitors: number | null;
  score: number | null;
  award: string | null;
  awarded_at: string;
}

export interface SeasonStats {
  totalEntries: number;
  scoredEntries: number;
  averageScore: number | null;
  bestPlacement: number | null;
  topThreeCount: number;
  awardCount: number;
  byCompetition: {
    competition_id: string;
    competition_name: string;
    entries: number;
    topThree: number;
  }[];
}

export function summarizeSeason(placements: PlacementRow[]): SeasonStats {
  const stats: SeasonStats = {
    totalEntries: placements.length,
    scoredEntries: 0,
    averageScore: null,
    bestPlacement: null,
    topThreeCount: 0,
    awardCount: 0,
    byCompetition: [],
  };

  if (placements.length === 0) return stats;

  let scoreSum = 0;
  let scoreCount = 0;
  const byCompMap = new Map<
    string,
    { competition_id: string; competition_name: string; entries: number; topThree: number }
  >();

  for (const p of placements) {
    if (p.score !== null) {
      scoreSum += p.score;
      scoreCount += 1;
      stats.scoredEntries += 1;
    }
    if (p.placement !== null) {
      if (stats.bestPlacement === null || p.placement < stats.bestPlacement) {
        stats.bestPlacement = p.placement;
      }
      if (p.placement <= 3) stats.topThreeCount += 1;
    }
    if (p.award !== null && p.award.trim() !== '') {
      stats.awardCount += 1;
    }

    const compEntry = byCompMap.get(p.competition_id) ?? {
      competition_id: p.competition_id,
      competition_name: p.competition_name,
      entries: 0,
      topThree: 0,
    };
    compEntry.entries += 1;
    if (p.placement !== null && p.placement <= 3) compEntry.topThree += 1;
    byCompMap.set(p.competition_id, compEntry);
  }

  stats.averageScore = scoreCount > 0 ? scoreSum / scoreCount : null;
  stats.byCompetition = [...byCompMap.values()].sort((a, b) =>
    a.competition_name.localeCompare(b.competition_name),
  );
  return stats;
}

// Format a placement for display ("1st", "2nd", "3rd of 12").
export function formatPlacement(placement: number | null, total: number | null): string {
  if (placement === null) return '—';
  const suffix = ordinalSuffix(placement);
  const main = `${placement}${suffix}`;
  return total !== null ? `${main} of ${total}` : main;
}

function ordinalSuffix(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}
